import * as turf from '@turf/turf';

/**
 * 几何算法工具集 — 纯函数，无副作用
 *
 * 层级：map-core（辅助模块）
 *
 * 职责：
 * 1. 线段相交检测
 * 2. 多边形自相交检测
 * 3. 重复点判断
 * 4. 像素距离计算
 * 5. 多边形有向面积
 *
 * 不知道 BMapGL 存在，不做任何 DOM / SDK 操作
 */

type Point = [number, number];

/**
 * 计算叉积 (B-A) × (C-A)
 * 正值 = C 在 AB 左侧，负值 = 右侧，0 = 共线
 */
function cross(A: Point, B: Point, C: Point): number {
  return (B[0] - A[0]) * (C[1] - A[1]) - (B[1] - A[1]) * (C[0] - A[0]);
}

/**
 * 判断线段 AB 与线段 CD 是否相交
 *
 * 算法：叉积法（cross product），O(1)
 * 共享端点不算相交（相邻边共享顶点是多边形的正常结构）
 */
export function segmentsIntersect(
  A: Point,
  B: Point,
  C: Point,
  D: Point
): boolean {
  // 共享端点 → 不算相交（相邻边共享顶点是正常情况）
  if ((A[0] === C[0] && A[1] === C[1]) ||
      (A[0] === D[0] && A[1] === D[1]) ||
      (B[0] === C[0] && B[1] === C[1]) ||
      (B[0] === D[0] && B[1] === D[1])) {
    return false;
  }

  const d1 = cross(A, B, C);
  const d2 = cross(A, B, D);
  const d3 = cross(C, D, A);
  const d4 = cross(C, D, B);

  // 一般相交：AB 的两端在 CD 两侧，且 CD 的两端在 AB 两侧
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  // 共线退化情况：端点在线段内部才算相交（不含端点本身）
  if (d1 === 0 && onSegmentInterior(A, B, C)) return true;
  if (d2 === 0 && onSegmentInterior(A, B, D)) return true;
  if (d3 === 0 && onSegmentInterior(C, D, A)) return true;
  if (d4 === 0 && onSegmentInterior(C, D, B)) return true;

  return false;
}

/**
 * 判断点 P 是否在线段 AB 的内部（不含端点 A、B）
 * 仅在共线情况下调用
 */
function onSegmentInterior(A: Point, B: Point, P: Point): boolean {
  return (
    P[0] > Math.min(A[0], B[0]) &&
    P[0] < Math.max(A[0], B[0]) &&
    P[1] > Math.min(A[1], B[1]) &&
    P[1] < Math.max(A[1], B[1])
  );
}

/**
 * 判断将 newPoint 追加到 existingPoints 末尾后，
 * 新边（last → newPoint）是否与已有非邻接边相交
 *
 * 用于绘制时实时检测：新点加入前先调用此函数
 */
export function wouldSelfIntersect(
  newPoint: Point,
  existingPoints: Point[]
): boolean {
  const n = existingPoints.length;
  if (n < 2) return false;

  const last = existingPoints[n - 1];

  // 遍历已有边 (existingPoints[i], existingPoints[i+1])
  // 跳过最后一条边（与新边共享端点，是邻接边）
  for (let i = 0; i < n - 1; i++) {
    if (segmentsIntersect(last, newPoint, existingPoints[i], existingPoints[i + 1])) {
      return true;
    }
  }
  return false;
}

/**
 * 判断闭合多边形（points + points[0]）是否存在自相交
 *
 * 输入 points 不含首尾闭合，即 [p0, p1, p2, ...]
 * 三角形不可能自相交，直接返回 false
 *
 * O(n²) 但 n < 50 时完全可接受
 */
export function isSelfIntersecting(points: Point[]): boolean {
  const n = points.length;
  if (n < 4) return false;

  // 构造闭合边集
  for (let i = 0; i < n; i++) {
    const a1 = points[i];
    const a2 = points[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      const b1 = points[j];
      const b2 = points[(j + 1) % n];

      // 跳过邻接边（共享顶点的边）
      if (i === j || (i + 1) % n === j || (j + 1) % n === i) continue;

      if (segmentsIntersect(a1, a2, b1, b2)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * 判断两个点是否重复（坐标相同）
 *
 * @param epsilon - 容差，默认 1e-10
 */
export function isDuplicatePoint(
  a: Point,
  b: Point,
  epsilon = 1e-10
): boolean {
  return (
    Math.abs(a[0] - b[0]) < epsilon &&
    Math.abs(a[1] - b[1]) < epsilon
  );
}

/**
 * 计算两个像素坐标的距离
 */
export function pixelDistance(
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y);
}

/**
 * 计算多边形实际面积（平方米）
 *
 * 使用 turf 球面面积计算，输入 points 不含首尾闭合点
 */
export function getPolygonArea(points: Point[]): number {
  if (points.length < 3) return 0;
  const polygon = turf.polygon([[...points, points[0]]]);
  return turf.area(polygon);
}

/**
 * 计算多边形有向面积（Shoelace formula）
 *
 * 正值 = 逆时针，负值 = 顺时针
 * 取绝对值即为面积
 */
export function polygonSignedArea(points: Point[]) {
  const n = points.length;
  if (n < 3) return 0;

  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i][0] * points[j][1];
    area -= points[j][0] * points[i][1];
  }
  return area / 2;
}

/**
 * 判断点集中是否存在重复点
 */
export function hasDuplicatePoints(points: Point[]): boolean {
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      if (isDuplicatePoint(points[i], points[j])) return true;
    }
  }
  return false;
}

/**
 * 去掉共线中间点
 * 当 A→B→C 几乎共线时，B 是冗余的中间点，去掉它不影响多边形形状
 * 使用相对误差：cross / (|AB| * |BC|) < threshold
 * 用于闭合前清理，避免 convex() 误判
 */
export function removeCollinearPoints(points: Point[], threshold = 1e-4): Point[] {
  if (points.length < 3) return [...points];

  const result: Point[] = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = result[result.length - 1];
    const curr = points[i];
    const next = points[i + 1];
    const c = cross(prev, curr, next);
    const ab = Math.hypot(curr[0] - prev[0], curr[1] - prev[1]);
    const bc = Math.hypot(next[0] - curr[0], next[1] - curr[1]);
    const denom = ab * bc;
    if (denom === 0 || Math.abs(c) / denom > threshold) {
      result.push(curr);
    }
    // |cross| / (|AB|*|BC|) < threshold → 几乎共线，跳过 curr
  }
  result.push(points[points.length - 1]);
  return result;
}
